import React, { useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
} from "@mui/material";
import RetroGridBg from "../RetroGridBg";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import UserAvatar from "../UserAvatar";
import { useDispatch, useSelector } from "react-redux";
import {
  setTeamMemberLawyerId,
  setUserAccess,
} from "../../redux/Apps/users/userSlice";
import { useNavigate } from "react-router-dom";

const FirmAccessFoundPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamData } = useSelector((data) => data.user);

  const handleJoinTeam = (item) => {
    console.log("Joining team:", item);
    dispatch(setUserAccess(item?.access));
    dispatch(setTeamMemberLawyerId(item?.lawyer?.id));
    navigate("/dashboard");
  };

  const handleLawyerLogin = () => {
    navigate("/dashboard");
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Box
        sx={{
          position: "fixed",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <RetroGridBg />
      </Box>

      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={handleLawyerLogin}
          startIcon={<AdminPanelSettingsIcon />}
          sx={{
            color: "white",
            fontWeight: 600,
            borderRadius: 2,
            px: 3,
            py: 1,
            boxShadow: 3,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 6,
            },
            transition: "all 0.3s ease",
          }}
        >
          Login as Lawyer
        </Button>
      </Box>

      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          pt: 4,
          pb: 4,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 700,
              color: "text.primary",
              textAlign: "center",
            }}
          >
            Choose Your Profile
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: "text.secondary",
              textAlign: "center",
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            Select a team profile to access your designated workspace and
            permissions
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {/* Team Cards */}
            {teamData &&
              teamData.length > 0 &&
              teamData.map((team) => (
                <Grid item xs={12} sm={6} lg={4} key={team.id}>
                  <Card
                    elevation={2}
                    onClick={() => handleJoinTeam(team)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        elevation: 8,
                        transform: "translateY(-4px)",
                      },
                      borderRadius: 3,
                      overflow: "hidden",
                      cursor: "pointer",
                    }}
                  >
                    {/* Card Header */}
                    <Box
                      sx={{
                        background:
                          "linear-gradient(135deg, #4DD4D1 0%, #00B3B0 100%)",
                        p: 1,
                        color: "white",
                        textAlign: "center",
                        position: "relative",
                      }}
                    >
                      <BusinessRoundedIcon
                        sx={{
                          fontSize: 40,
                          mb: 2,
                          opacity: 0.9,
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                        }}
                      >
                        <Chip
                          label="Team"
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255,255,255,0.2)",
                            color: "white",
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent
                      sx={{ flexGrow: 1, p: 3, textAlign: "center" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          mb: 2,
                          mt: -6,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: "3.5rem",
                            height: "3.5rem",
                            border: "4px solid white",
                            boxShadow: 2,
                          }}
                        >
                          <UserAvatar
                            imgSrc={team?.lawyer?.avatar}
                            height={"3.5rem"}
                            width={"3.5rem"}
                          />
                        </Avatar>
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        {team?.lawyer?.first_name && team?.lawyer?.last_name
                          ? `${team.lawyer.first_name} ${team.lawyer.last_name}`
                          : "Legal Team"}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        {team?.access?.map((roleItem, index) => (
                          <Chip
                            key={index}
                            label={roleItem.role_name}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        )) || (
                          <Chip
                            label="No Role"
                            color="default"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

            {/* Empty State */}
            {(!teamData || teamData.length === 0) && (
              <Grid item xs={12} md={8} lg={6}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 6,
                    textAlign: "center",
                    borderRadius: 3,
                    backgroundColor: "white",
                  }}
                >
                  <BusinessRoundedIcon
                    sx={{
                      fontSize: 64,
                      color: "grey.400",
                      mb: 3,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    No Team Profiles Available
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    You don't have any team invitations at the moment. Contact
                    your administrator for access.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleLawyerLogin}
                    sx={{
                      borderColor: "#4DD4D1",
                      color: "#4DD4D1",
                      "&:hover": {
                        borderColor: "#00B3B0",
                        backgroundColor: "rgba(77, 212, 209, 0.1)",
                      },
                    }}
                  >
                    Access Lawyer Dashboard Instead
                  </Button>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default FirmAccessFoundPage;
